import { makeObservable } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel, NodeId } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type {
  UnitNodeProps,
  UnitNodeConstructorProps,
  UnitNodePlacement,
  UnitTypeValue,
} from "@corca/data";

export class UnitNodeModel<UT extends UnitTypeValue = UnitTypeValue>
  extends BaseNodeModel<UnitNodeProps<UT>>
  implements VisitableNode
{
  static type = "unit" as const;

  type = UnitNodeModel.type;

  protected fillRequiredProps(props: UnitNodeConstructorProps<UT>): UnitNodeProps<UT> {
    return { ...props, placement: props.placement ?? "postfix" };
  }

  constructor(...args: ConstructorParameters<typeof BaseNodeModel<UnitNodeConstructorProps<UT>>>) {
    super(...args);
    makeObservable(this, {
      inlineNode: true,
    });
  }

  static create<T extends UnitTypeValue>(
    doc: DocumentModel,
    nodes: NodeModel[] = [],
    props: UnitNodeConstructorProps<T> = {},
    id?: NodeId,
  ) {
    return new UnitNodeModel(doc, id, props, [doc.create(InlineNodeModel, nodes)]);
  }

  get name(): string {
    return this.props.name || "";
  }

  set name(name: string) {
    this.updateProps({ name });
  }

  get unitType(): UT | undefined {
    return this.props.unitType;
  }

  set unitType(unitType: UT | undefined) {
    this.updateProps({ unitType });
  }

  get placement(): UnitNodePlacement {
    return this.props.placement || "postfix";
  }

  set placement(placement: UnitNodePlacement) {
    this.updateProps({ placement });
  }

  get inlineNode() {
    return this.children.find((n) => n.is(InlineNodeModel));
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitUnitNode(this);
  }
}
